import React, { useState } from "react";
import { Box, TextField, Typography, Modal } from "@mui/material";
import { styled } from "@mui/material/styles";
import { SaveButton, CloseButton } from "../../../common/Buttons";

const ModalContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  backgroundColor: theme.palette.background.paper,
  borderRadius: "8px",
  boxShadow: 24,
  padding: theme.spacing(4),
}));

function CreateItemModal({ table, handleAddItem, openSnackbar }) {
  const [validationErrors, setValidationErrors] = useState({
    safety_stock: false,
    pce: false,
    lot_size: false,
  });
  const validateSafetyStock = (value) => {
    if (value === "" || value === null || value === undefined) return false;
    const numValue = parseInt(value, 10);
    return !isNaN(numValue) && numValue >= 0 && Number.isInteger(numValue);
  };
  const validatePCE = (value) => {
    if (value === "" || value === null || value === undefined) return false;
    const numValue = parseInt(value, 10);
    return (
      !isNaN(numValue) &&
      numValue >= 1 &&
      numValue <= 100 &&
      Number.isInteger(numValue)
    );
  };
  const validateLotSize = (value) => {
    if (value === "" || value === null || value === undefined) return false;
    const numValue = parseInt(value, 10);
    return !isNaN(numValue) && numValue >= 1 && Number.isInteger(numValue);
  };
  const handleSafetyStockChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      const currentRowData = { ...table.getState().creatingRow };
      currentRowData.safety_stock = value;
      table.setCreatingRow(currentRowData);

      setValidationErrors((prev) => ({
        ...prev,
        safety_stock: !validateSafetyStock(value),
      }));
    }
  };
  const handlePCEChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^(?:[1-9]|[1-9]\d|100)$/.test(value)) {
      const currentRowData = { ...table.getState().creatingRow };
      currentRowData.pce = value;
      table.setCreatingRow(currentRowData);
      setValidationErrors((prev) => ({
        ...prev,
        pce: !validatePCE(value),
      }));
    }
  };
  const handleLotSizeChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      const currentRowData = { ...table.getState().creatingRow };
      currentRowData.lot_size = value;
      table.setCreatingRow(currentRowData);

      setValidationErrors((prev) => ({
        ...prev,
        lot_size: !validateLotSize(value),
      }));
    }
  };
  const handleSave = () => {
    const values = table.getState().creatingRow;
    console.log("저장 시 values:", values);
    if (!values.item_name?.trim()) {
      openSnackbar("Item 이름을 입력해 주세요.", "warning");
      return;
    }
    if (!values.safety_stock) {
      openSnackbar("안전재고를 입력해 주세요.", "warning");
      return;
    }
    if (!validateSafetyStock(values.safety_stock)) {
      openSnackbar("안전재고는 0 이상의 정수여야 합니다.", "warning");
      return;
    }
    if (!values.pce) {
      openSnackbar("PCE를 입력해 주세요.", "warning");
      return;
    }
    const pceValue = parseInt(values.pce, 10);
    if (
      isNaN(pceValue) ||
      pceValue < 1 ||
      pceValue > 100 ||
      !Number.isInteger(pceValue)
    ) {
      openSnackbar("PCE는 1 이상 100 이하의 정수여야 합니다.", "warning");
      return;
    }
    if (!values.lot_size) {
      openSnackbar("LOT 사이즈를 입력해 주세요.", "warning");
      return;
    }
    if (!validateLotSize(values.lot_size)) {
      openSnackbar("LOT 사이즈는 1 이상의 정수여야 합니다.", "warning");
      return;
    }
    handleAddItem({ values, table });
  };

  return (
    <Modal open={true}>
      <ModalContainer>
        <Typography variant="h6" component="h2" mb={3}>
          Item 추가
        </Typography>
        <TextField
          label="Item 코드"
          fullWidth
          autoFocus
          name="item_code"
          onChange={(e) => {
            const currentRowData = { ...table.getState().creatingRow };
            currentRowData.item_code = e.target.value;
            table.setCreatingRow(currentRowData);
          }}
          sx={{ mb: 3 }}
        />
        <TextField
          label="Item 이름"
          fullWidth
          name="item_name"
          onChange={(e) => {
            const currentRowData = { ...table.getState().creatingRow };
            currentRowData.item_name = e.target.value;
            table.setCreatingRow(currentRowData);
          }}
          sx={{ mb: 3 }}
        />
        <TextField
          label="Material Grouping"
          fullWidth
          name="material_grouping"
          onChange={(e) => {
            const currentRowData = { ...table.getState().creatingRow };
            currentRowData.material_grouping = e.target.value;
            table.setCreatingRow(currentRowData);
          }}
          sx={{ mb: 3 }}
        />
        <TextField
          label="안전재고"
          fullWidth
          name="safety_stock"
          onChange={handleSafetyStockChange}
          sx={{ mb: 3 }}
          error={validationErrors.safety_stock}
          helperText={
            validationErrors.safety_stock ? "0 이상의 정수를 입력하세요" : ""
          }
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          required
        />
        <TextField
          label="PCE (1 이상 100 이하의 정수)"
          fullWidth
          name="pce"
          onChange={handlePCEChange}
          sx={{ mb: 3 }}
          error={validationErrors.pce}
          helperText={
            validationErrors.pce ? "1 이상 100 이하의 정수를 입력하세요" : ""
          }
          inputProps={{
            inputMode: "numeric",
            pattern: "^(?:[1-9]|[1-9]\\d|100)$",
          }}
          required
        />
        <TextField
          label="LOT 사이즈"
          fullWidth
          name="lot_size"
          onChange={handleLotSizeChange}
          sx={{ mb: 3 }}
          error={validationErrors.lot_size}
          helperText={
            validationErrors.lot_size ? "1 이상의 정수를 입력하세요" : ""
          }
          inputProps={{
            inputMode: "numeric",
            pattern: "[1-9][0-9]*",
          }}
          required
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <SaveButton onClick={handleSave} />
          <CloseButton
            variant="outlined"
            onClick={() => table.setCreatingRow(null)}
            color="inherit"
          />
        </Box>
      </ModalContainer>
    </Modal>
  );
}

export default CreateItemModal;
